import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";

import SSidebar from "../sComponents/sSidebar";
import SCalendarLegend from "../sComponents/sCalendar/sCalendarLegend";
import SCalendarContainer from "../sComponents/sCalendar/sCalendarContainer";

import CreateEventModal from "../sComponents/sModals/CreateEventModal";
import CreateWorkEventModal from "../sComponents/sModals/CreateWorkEventModal";
import ViewingWorkEventModal from "../sComponents/sModals/ViewingWorkEventModal";
import ViewingEventModal from "../sComponents/sModals/ViewingEventModal";
import EditEventModal from "../sComponents/sModals/EditEventModal";
import EditWorkEventModal from "../sComponents/sModals/EditWorkEventModal";

import { getTitleColor } from "../sUtils/eventColors";
import { filterEvents } from "../sUtils/filtersEvents";
import { mapEventsForCalendar, mapHolidays, mapBirthdays } from "../sComponents/sCalendar/sEventMapper";
import useSchedulerActions from "../sHooks/useSchedulerActions";
import { fetchCurrentUserApi, fetchEventsApi, fetchHolidaysApi, fetchBirthdaysApi, fetchEmployeesApi } from "../sServices/schedulerApi";

import useToastTimer from "../sHooks/useToastTimer";

function SchedulerDashboard() {
  const [events, setEvents] = useState([]);
  const [work, setWork] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [currentUser, setCurrentUser] = useState(JSON.parse(sessionStorage.getItem("user")) || null);

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedWorkEvent, setSelectedWorkEvent] = useState(null);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreateWorkEventModalOpen, setIsCreateWorkEventModalOpen] = useState(false);
  const [isViewingEventModalOpen, setIsViewingEventModalOpen] = useState(false);
  const [isViewingWorkEventModalOpen, setIsViewingWorkEventModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditWorkEventModalOpen, setIsEditWorkEventModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [setEditingWorkEvent] = useState(null);


  const [newEvent, setNewEvent] = useState({
    event_type: "",
    title: "",
    date_and_time: "",
    primary_technician: "",
    description: "",
  });

  const [newWorkEvent, setNewWorkEvent] = useState({
    event_type: "",
    description: "",
    job_number: "",
    date_and_time: "",
    primary_technician: "",
    additional_technicians: "",
    property: "",
    street_address: "",
    city: "",
    state: "",
    zipcode: "",
  });

  const [toast, setToast] = useState({ message: "", type: "success" });
  const [searchTerm, setSearchTerm] = useState("");

  useToastTimer(toast, setToast);

  const navigate = useNavigate();
  const auth = getAuth();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:5000";

  // === Fetch all initial data ===
  const fetchAllEvents = useCallback(async () => {
    try {
      const [user, { events, work }, holidaysData, birthdaysData, employeesData] = await Promise.all([
        fetchCurrentUserApi(auth, API_BASE_URL),
        fetchEventsApi(auth, API_BASE_URL),
        fetchHolidaysApi(API_BASE_URL),
        fetchBirthdaysApi(API_BASE_URL),
        fetchEmployeesApi(API_BASE_URL),
      ]);

      setCurrentUser(user);
      sessionStorage.setItem("user", JSON.stringify(user));

      setEvents(events);
      setWork(work);
      setHolidays(holidaysData);
      setBirthdays(birthdaysData);
      setEmployees(employeesData);
    } catch (err) {
      console.error("Failed to fetch scheduler data:", err);
      setToast({ message: "❌ Failed to load data", type: "error" });
    }
  }, [auth, API_BASE_URL]);

  useEffect(() => {
    // Wait for Firebase to restore auth state before fetching data
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        fetchAllEvents();
      } else {
        navigate("/login");
      }
    });
    return () => unsubscribe();
  }, [auth, navigate, fetchAllEvents]);

  // === Filtered events for calendar ===
  const filteredEvents = useMemo(
    () =>
      filterEvents(
        events,
        work,
        mapHolidays(holidays),
        mapBirthdays(birthdays),
        searchTerm,
        mapEventsForCalendar
      ),
    [events, work, holidays, birthdays, searchTerm]
  );

  // === Scheduler actions hook ===
  const {
    handleFileUpload,
    handleDeleteAll,
    handleSaveWorkEdit,
    handleSaveEventEdit,
    handleDeleteWorkEvent,
    handleDeleteEvent,
    handleSignOut,
  } = useSchedulerActions({
    auth,
    API_BASE_URL,
    refresh: fetchAllEvents,
    setToast,
    setIsEditing,
    setIsModalOpen: setIsEditModalOpen, // for event editing
    selectedEvent,
    setSelectedEvent,
    navigate,
  });

  console.log("Selected Event:", selectedEvent);

  return (
    <div className="flex min-h-screen bg-gray-100 font-sans">
      {/* Sidebar */}
      <SSidebar
        currentUser={currentUser}
        handleFileUpload={handleFileUpload}
        handleDeleteAll={handleDeleteAll}
        setNewEvent={setNewEvent}
        setIsCreateModalOpen={setIsCreateModalOpen}
        setNewWorkEvent={setNewWorkEvent}
        setIsCreateWorkEventModalOpen={setIsCreateWorkEventModalOpen}
        handleSignOut={handleSignOut}
      />

      {/* Main Content */}
      <main className="flex-1 p-6 font-sans flex flex-col bg-white">
        <h2 className="text-2xl font-semibold mb-4">CALENDAR</h2>

        <SCalendarLegend />

        {/* Search Input */}
        <div className="mb-4">
          <input
            id="calendar-search"
            type="text"
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border rounded px-3 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        <SCalendarContainer
          filteredEvents={filteredEvents}
          setSelectedEvent={setSelectedEvent}
          setSelectedWorkEvent={setSelectedWorkEvent}
          setIsViewingEventModalOpen={setIsViewingEventModalOpen}
          setIsViewingWorkEventModalOpen={setIsViewingWorkEventModalOpen}
          setIsEditing={setIsEditing}
          getTitleColor={getTitleColor}
        />

        {/* Create / Edit Modals */}
        <CreateEventModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          newEvent={newEvent}
          setNewEvent={setNewEvent}
          currentUser={currentUser}
          auth={auth}
          API_BASE_URL={API_BASE_URL}
          employees={employees}
          fetchAllEvents={fetchAllEvents}
          setToast={setToast}
        />

        <CreateWorkEventModal
          isOpen={isCreateWorkEventModalOpen}
          onClose={() => setIsCreateWorkEventModalOpen(false)}
          newWorkEvent={newWorkEvent}
          setNewWorkEvent={setNewWorkEvent}
          currentUser={currentUser}
          auth={auth}
          API_BASE_URL={API_BASE_URL}
          employees={employees}
          fetchAllEvents={fetchAllEvents}
          setToast={setToast}
        />

        {/* Non-work event modal */}
        <ViewingEventModal
          isOpen={
            isViewingEventModalOpen &&
            selectedEvent &&
            selectedEvent.event_type !== "work" &&
            !isEditing
          }
          onClose={() => setIsViewingEventModalOpen(false)}
          selectedEvent={selectedEvent}
          setIsEditing={setIsEditing}
          setIsEditModalOpen={setIsEditModalOpen}
          handleDeleteEvent={handleDeleteEvent}
        />

        {/* Work event modal */}
        <ViewingWorkEventModal
          isOpen={
            isViewingWorkEventModalOpen &&
            selectedWorkEvent &&
            !isEditWorkEventModalOpen
          }
          onClose={() => {
            setIsViewingWorkEventModalOpen(false);
            setSelectedWorkEvent(null);
          }}
          selectedEvent={selectedWorkEvent}
          setEditingWorkEvent={setEditingWorkEvent}
          setIsEditWorkEventModalOpen={setIsEditWorkEventModalOpen}
          setIsViewingWorkEventModalOpen={setIsViewingWorkEventModalOpen}
          handleDeleteWorkEvent={handleDeleteWorkEvent}
        />

        {/* Non-work edit modal */}
        <EditEventModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          selectedEvent={selectedEvent}
          setSelectedEvent={setSelectedEvent}
          setIsViewingEventModalOpen={setIsViewingEventModalOpen}
          currentUser={currentUser}
          auth={auth}
          API_BASE_URL={API_BASE_URL}
          employees={employees}
          fetchAllEvents={fetchAllEvents}
          setToast={setToast}
          handleSaveEventEdit={handleSaveEventEdit}
        />

        {/* Work edit modal */}
        <EditWorkEventModal
          isOpen={isEditWorkEventModalOpen}
          onClose={() => setIsEditWorkEventModalOpen(false)}
          selectedWorkEvent={selectedWorkEvent}
          setSelectedWorkEvent={setSelectedWorkEvent}
          currentUser={currentUser}
          auth={auth}
          API_BASE_URL={API_BASE_URL}
          employees={employees}
          fetchAllEvents={fetchAllEvents}
          setToast={setToast}
          setIsViewingWorkEventModalOpen={setIsViewingWorkEventModalOpen}
          handleSaveWorkEdit={handleSaveWorkEdit}
        />

        {/* === Toast Notification === */}
        {toast.message && (
          <div
            className={`fixed top-5 right-5 px-4 py-2 rounded shadow-lg text-white font-sans transition-opacity duration-300 ${toast.type === "success" ? "bg-green-500" : "bg-red-500"
              }`}
          >
            {toast.message}
          </div>
        )}
      </main>
    </div>
  );
}

export default SchedulerDashboard;